import React, { useEffect, useMemo, useRef, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { apiErrorMessage } from "../lib/api-errors"
import { canApproveDocument } from "../lib/document-approval"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { DocumentLineRows } from "../components/document-line-rows"
import { RequisitionsPanel } from "../components/requisitions-panel"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { cn } from "../lib/utils"
import { appConfirm, appToast } from "../lib/app-dialogs"
import { usePageLoading } from "../lib/page-loading"
import { GST_STATE_OPTIONS } from "../lib/gst-states"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {})
    },
    ...options
  })

  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null

  if (!res.ok) {
    const message = data?.message || (data?.error ? `${data.error}` : `Request failed (${res.status})`)
    const details = data?.details ? JSON.stringify(data.details) : ""
    throw new Error(details ? `${message}: ${details}` : message)
  }

  return data
}

function centsToAmount(cents) {
  if (cents === null || cents === undefined) return ""
  return (Number(cents) / 100).toFixed(2)
}

function amountToCents(amount) {
  const n = Number.parseFloat(String(amount || "0").trim())
  if (Number.isNaN(n)) return 0
  return Math.max(0, Math.round(n * 100))
}

function TextInput({ label, value, onChange, type = "text", placeholder, error }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      />
      {error ? <span className="text-[12px] text-red-600">{error}</span> : null}
    </label>
  )
}

function SelectInput({ label, value, onChange, options, error }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <span className="text-xs text-red-600">{error}</span> : null}
    </label>
  )
}

function nextDocNumber(items, fieldName, prefix) {
  let max = 0
  items.forEach((item) => {
    const raw = String(item[fieldName] || "")
    const match = raw.match(/(\d+)\s*$/)
    if (!match) return
    const n = Number.parseInt(match[1], 10)
    if (!Number.isNaN(n)) max = Math.max(max, n)
  })
  return `${prefix}${String(max + 1).padStart(4, "0")}`
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

const QUOTE_TYPE_OPTIONS = [
  { value: "stores", label: "Stores" },
  { value: "spares", label: "Spares" },
  { value: "provision", label: "Provision" },
  { value: "services", label: "Services" },
  { value: "repairs", label: "Repairs" },
  { value: "annual_repairs", label: "Annual repairs" },
]

function CreateCustomerQuoteDialog({ vendorQuote, customers, saving, error, onClose, onConfirm }) {
  const [customerId, setCustomerId] = useState("")
  const customerOptions = customers.map((c) => ({ value: String(c.id), label: c.name }))

  if (!vendorQuote) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-customer-quote-title"
    >
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle id="create-customer-quote-title">Create customer quote</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <p className="text-xs text-muted-foreground">
            Generate your outbound quote from vendor reference{" "}
            <span className="font-medium text-foreground">{vendorQuote.vendor_reference}</span> (
            {vendorQuote.vendor?.name || "vendor"}) for a third-party customer.
          </p>
          <SelectInput
            label="Customer (third party)"
            value={customerId}
            onChange={setCustomerId}
            options={customerOptions}
          />
          {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={saving || !customerId}
              onClick={() => onConfirm(Number.parseInt(customerId, 10))}
            >
              {saving ? "Creating…" : "Create quote"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function ResourceCrud({ sectionKey, sectionTitle, access = {}, enableVendorQuoteReselling = false }) {
  const canEnter = Boolean(access.can_enter_transactions)
  const canApprove = Boolean(access.can_approve_documents)

  const [items, setItems] = useState([])
  const [vendors, setVendors] = useState([])
  const [customers, setCustomers] = useState([])
  const [pendingCustomerQuote, setPendingCustomerQuote] = useState(null)
  const [customerQuoteSaving, setCustomerQuoteSaving] = useState(false)
  const [customerQuoteError, setCustomerQuoteError] = useState("")
  const [gstRates, setGstRates] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [fieldErrors, setFieldErrors] = useState({})
  const [query, setQuery] = useState("")
  const [editingId, setEditingId] = useState(null)
  const editingIdRef = useRef(editingId)
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 10

  useEffect(() => {
    editingIdRef.current = editingId
  }, [editingId])

  const config = useMemo(() => {
    switch (sectionKey) {
      case "vendor-quotes":
        return {
          key: "vendor_quotes",
          endpoint: "/api/purchases/vendor_quotes",
          wrap: "vendor_quote",
          smartDefaults: () => ({
            quote_date: todayISO(),
            status: "received",
            currency: "INR",
          }),
          fields: [
            { name: "vendor_id", label: "Vendor", kind: "vendor", required: true },
            { name: "vendor_reference", label: "Vendor quote ref #", kind: "text", required: true },
            { name: "quote_date", label: "Quote Date", kind: "date", required: true },
            {
              name: "quote_type",
              label: "Quote type",
              kind: "select",
              required: true,
              options: QUOTE_TYPE_OPTIONS,
            },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "received", label: "Received" },
                { value: "archived", label: "Archived" },
              ],
            },
            { name: "total_cents", label: "Total", kind: "amount", required: true },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" },
          ],
          columns: ["vendor_reference", "quote_date", "vendor", "quote_type", "status", "total_cents"],
        }
      case "vendors":
        return {
          key: "vendors",
          endpoint: "/api/purchases/vendors",
          wrap: "vendor",
          fields: [
            { name: "name", label: "Name", kind: "text", required: true },
            { name: "email", label: "Email", kind: "text" },
            { name: "phone", label: "Phone", kind: "text" },
            { name: "address", label: "Address", kind: "text" },
            { name: "tax_number", label: "Tax Number", kind: "text" },
            { name: "state_code", label: "State (GST)", kind: "select", options: GST_STATE_OPTIONS }
          ],
          columns: ["name", "email", "phone", "state_code"]
        }
      case "expenses":
        return {
          key: "expenses",
          endpoint: "/api/purchases/expenses",
          wrap: "expense",
          smartDefaults: () => ({ expense_date: todayISO() }),
          fields: [
            { name: "vendor_id", label: "Vendor", kind: "vendor", required: true },
            { name: "expense_date", label: "Expense Date", kind: "date", required: true },
            { name: "description", label: "Description", kind: "text", required: true },
            { name: "amount_cents", label: "Amount", kind: "amount", required: true },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["expense_date", "vendor", "description", "amount_cents"]
        }
      case "recurring-expenses":
        return {
          key: "recurring_expenses",
          endpoint: "/api/purchases/recurring_expenses",
          wrap: "recurring_expense",
          smartDefaults: () => ({
            start_date: todayISO(),
            interval_count: 1,
            interval_unit: "month",
            active: true,
          }),
          fields: [
            { name: "vendor_id", label: "Vendor", kind: "vendor", required: true },
            { name: "start_date", label: "Start Date", kind: "date", required: true },
            { name: "description", label: "Description", kind: "text", required: true },
            { name: "amount_cents", label: "Amount", kind: "amount", required: true },
            { name: "interval_count", label: "Interval Count", kind: "number" },
            {
              name: "interval_unit",
              label: "Interval Unit",
              kind: "select",
              options: [
                { value: "day", label: "Day" },
                { value: "week", label: "Week" },
                { value: "month", label: "Month" },
                { value: "year", label: "Year" }
              ]
            },
            { name: "next_run_on", label: "Next Run On", kind: "date" },
            { name: "active", label: "Active", kind: "boolean" },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["start_date", "vendor", "description", "amount_cents", "active"]
        }
      case "purchase-orders":
        return {
          key: "purchase_orders",
          endpoint: "/api/purchases/purchase_orders",
          wrap: "purchase_order",
          smartDefaults: (allItems) => ({
            order_number: nextDocNumber(allItems, "order_number", "PO-"),
            order_date: todayISO(),
            status: "draft",
          }),
          fields: [
            { name: "vendor_id", label: "Vendor", kind: "vendor", required: true },
            { name: "order_number", label: "Order #", kind: "text", required: true },
            { name: "order_date", label: "Order Date", kind: "date", required: true },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "draft", label: "Draft" },
                { value: "sent", label: "Sent" },
                { value: "closed", label: "Closed" }
              ]
            },
            { name: "total_cents", label: "Total", kind: "amount", required: true },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["order_number", "order_date", "vendor", "status", "total_cents"]
        }
      case "bills":
        return {
          key: "bills",
          endpoint: "/api/purchases/bills",
          wrap: "bill",
          smartDefaults: (allItems) => ({
            bill_number: nextDocNumber(allItems, "bill_number", "BILL-"),
            bill_date: todayISO(),
            status: "draft",
            lines: [{ description: "", amount: "", hsn_sac: "", gst_tax_rate_id: "" }]
          }),
          fields: [
            { name: "vendor_id", label: "Vendor", kind: "vendor", required: true },
            { name: "bill_number", label: "Bill #", kind: "text", required: true },
            { name: "bill_date", label: "Bill Date", kind: "date", required: true },
            { name: "due_date", label: "Due Date", kind: "date" },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "draft", label: "Draft" },
                { value: "unpaid", label: "Unpaid" },
                { value: "paid", label: "Paid" },
                { value: "overdue", label: "Overdue" },
                { value: "void", label: "Void" },
                { value: "cancelled", label: "Cancelled" }
              ]
            },
            { name: "total_cents", label: "Total", kind: "amount", required: true },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["bill_number", "bill_date", "vendor", "status", "approval_status", "total_cents"]
        }
      case "recurring-bills":
        return {
          key: "recurring_bills",
          endpoint: "/api/purchases/recurring_bills",
          wrap: "recurring_bill",
          smartDefaults: () => ({
            start_date: todayISO(),
            interval_count: 1,
            interval_unit: "month",
            active: true,
          }),
          fields: [
            { name: "vendor_id", label: "Vendor", kind: "vendor", required: true },
            { name: "bill_number_prefix", label: "Prefix", kind: "text" },
            { name: "start_date", label: "Start Date", kind: "date", required: true },
            { name: "next_run_on", label: "Next Run On", kind: "date" },
            { name: "description", label: "Description", kind: "text", required: true },
            { name: "total_cents", label: "Total", kind: "amount", required: true },
            { name: "interval_count", label: "Interval Count", kind: "number" },
            {
              name: "interval_unit",
              label: "Interval Unit",
              kind: "select",
              options: [
                { value: "day", label: "Day" },
                { value: "week", label: "Week" },
                { value: "month", label: "Month" },
                { value: "year", label: "Year" }
              ]
            },
            { name: "active", label: "Active", kind: "boolean" },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["start_date", "vendor", "description", "total_cents", "active"]
        }
      case "payments-made":
        return {
          key: "payments_made",
          endpoint: "/api/purchases/payments_made",
          wrap: "payment_made",
          smartDefaults: (allItems) => ({
            payment_number: nextDocNumber(allItems, "payment_number", "PM-"),
            payment_date: todayISO(),
          }),
          fields: [
            { name: "vendor_id", label: "Vendor", kind: "vendor", required: true },
            { name: "payment_number", label: "Payment #", kind: "text", required: true },
            { name: "payment_date", label: "Payment Date", kind: "date", required: true },
            { name: "amount_cents", label: "Amount", kind: "amount", required: true },
            { name: "method", label: "Method", kind: "text" },
            { name: "reference", label: "Reference", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["payment_number", "payment_date", "vendor", "amount_cents", "method"]
        }
      case "vendor-credits":
        return {
          key: "vendor_credits",
          endpoint: "/api/purchases/vendor_credits",
          wrap: "vendor_credit",
          smartDefaults: (allItems) => ({
            credit_number: nextDocNumber(allItems, "credit_number", "VCN-"),
            credit_date: todayISO(),
            status: "open",
            lines: [{ description: "", amount: "", hsn_sac: "", gst_tax_rate_id: "" }]
          }),
          fields: [
            { name: "vendor_id", label: "Vendor", kind: "vendor", required: true },
            { name: "credit_number", label: "Credit #", kind: "text", required: true },
            { name: "credit_date", label: "Credit Date", kind: "date", required: true },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "open", label: "Open" },
                { value: "applied", label: "Applied" },
                { value: "void", label: "Void" }
              ]
            },
            { name: "amount_cents", label: "Total (from lines)", kind: "amount", required: false },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["credit_number", "credit_date", "vendor", "status", "amount_cents"]
        }
      default:
        return null
    }
  }, [sectionKey])

  const gstRateSelectOptions = useMemo(
    () => gstRates.map((r) => ({ value: String(r.id), label: `${r.code} (${r.rate_percent}%)` })),
    [gstRates],
  )

  const [form, setForm] = useState({})

  useEffect(() => {
    let alive = true
    async function load() {
      if (!config) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError("")
        const [itemsData, vendorData, customerData] = await Promise.all([
          fetchJson(config.endpoint),
          sectionKey === "vendors" ? Promise.resolve([]) : fetchJson("/api/purchases/vendors"),
          sectionKey === "vendor-quotes" ? fetchJson("/api/purchases/vendor_quotes/customers") : Promise.resolve([]),
        ])

        if (!alive) return
        setItems(itemsData || [])
        setVendors(vendorData || [])
        setCustomers(customerData || [])
        if (!editingIdRef.current) {
          setForm(config.smartDefaults ? config.smartDefaults(itemsData || []) : {})
        }
        setPage(1)
      } catch (e) {
        if (!alive) return
        setError(e.message || "Failed to load data")
      } finally {
        if (!alive) return
        setLoading(false)
      }
    }

    load()
    return () => {
      alive = false
    }
  }, [config, sectionKey])

  useEffect(() => {
    setPage(1)
  }, [query, sectionKey])

  useEffect(() => {
    if (sectionKey !== "bills" && sectionKey !== "vendor-credits") {
      setGstRates([])
      return
    }
    let alive = true
    ;(async () => {
      try {
        const data = await fetchJson("/api/accounting/gst_tax_rates")
        if (alive) setGstRates(Array.isArray(data) ? data : [])
      } catch {
        if (alive) setGstRates([])
      }
    })()
    return () => {
      alive = false
    }
  }, [sectionKey])

  function vendorNameFor(id) {
    const v = vendors.find((x) => String(x.id) === String(id))
    return v ? v.name : ""
  }

  function resetForm() {
    setEditingId(null)
    setFieldErrors({})
    setForm(config?.smartDefaults ? config.smartDefaults(items) : {})
  }

  function setField(name, value) {
    setFieldErrors((prev) => ({ ...prev, [name]: null }))
    setForm((f) => ({ ...f, [name]: value }))
  }

  function hydrateForEdit(item) {
    const next = {}
    config.fields.forEach((f) => {
      if (f.kind === "amount") {
        next[f.name] = centsToAmount(item[f.name])
      } else if (f.kind === "vendor") {
        next[f.name] = item.vendor_id ? String(item.vendor_id) : ""
      } else if (f.kind === "boolean") {
        next[f.name] = Boolean(item[f.name])
      } else if (f.kind === "number") {
        const v = item[f.name]
        next[f.name] = v === null || v === undefined ? "" : String(v)
      } else {
        next[f.name] = item[f.name] ?? ""
      }
    })
    if (sectionKey === "bills") {
      const raw = item.bill_lines || []
      next.lines = raw.length
        ? raw.map((l) => ({
            id: l.id,
            description: l.description || "",
            amount: centsToAmount(l.amount_cents),
            hsn_sac: l.hsn_sac || "",
            gst_tax_rate_id: l.gst_tax_rate_id ? String(l.gst_tax_rate_id) : "",
          }))
        : [{ description: "", amount: "", hsn_sac: "", gst_tax_rate_id: "" }]
    }
    if (sectionKey === "vendor-credits") {
      const raw = item.vendor_credit_lines || []
      next.lines = raw.length
        ? raw.map((l) => ({
            id: l.id,
            description: l.description || "",
            amount: centsToAmount(l.amount_cents),
            hsn_sac: l.hsn_sac || "",
            gst_tax_rate_id: l.gst_tax_rate_id ? String(l.gst_tax_rate_id) : "",
          }))
        : [{ description: "", amount: "", hsn_sac: "", gst_tax_rate_id: "" }]
    }
    setEditingId(item.id)
    setFieldErrors({})
    setForm(next)
  }

  async function reload() {
    const data = await fetchJson(config.endpoint)
    setItems(data || [])
  }

  async function approveBill(id) {
    try {
      setError("")
      await fetchJson(`/api/purchases/bills/${id}/approve`, { method: "POST", body: "{}" })
      await reload()
    } catch (e2) {
      setError(apiErrorMessage(e2, "Approve failed"))
    }
  }

  async function rejectBill(id) {
    try {
      setError("")
      await fetchJson(`/api/purchases/bills/${id}/reject`, { method: "POST", body: "{}" })
      await reload()
    } catch (e2) {
      setError(apiErrorMessage(e2, "Reject failed"))
    }
  }

  async function onSubmit(e) {
    e.preventDefault()
    try {
      setError("")
      const nextFieldErrors = {}
      const payload = {}
      config.fields.forEach((f) => {
        const raw = form[f.name]
        if (f.kind === "amount") payload[f.name] = amountToCents(raw)
        else if (f.kind === "number") {
          if (raw === "" || raw === null || raw === undefined) {
            payload[f.name] = f.name === "interval_count" ? 1 : null
          } else {
            const n = Number.parseInt(String(raw), 10)
            payload[f.name] = Number.isNaN(n) ? (f.name === "interval_count" ? 1 : null) : n
          }
        } else if (f.kind === "boolean") payload[f.name] = Boolean(raw)
        else if (f.kind === "vendor") payload[f.name] = raw === "" ? null : Number.parseInt(raw, 10)
        else if (f.name === "currency") payload[f.name] = raw === "" || raw == null ? "INR" : raw
        else payload[f.name] = raw === "" ? null : raw
      })

      config.fields.forEach((f) => {
        if (!f.required) return
        const value = payload[f.name]
        if (f.kind === "boolean") return
        if (value === null || value === undefined || value === "") {
          nextFieldErrors[f.name] = `${f.label} is required`
        }
      })
      config.fields.forEach((f) => {
        if (f.kind !== "amount" || !f.required) return
        if (payload[f.name] === 0) {
          nextFieldErrors[f.name] = `${f.label} must be greater than zero`
        }
      })
      if (Object.keys(nextFieldErrors).length > 0) {
        setFieldErrors(nextFieldErrors)
        throw new Error("Please correct highlighted fields.")
      }
      setFieldErrors({})

      if (sectionKey === "bills") {
        payload.lines = (form.lines || [])
          .map((row) => {
            const gid = row.gst_tax_rate_id ? Number.parseInt(String(row.gst_tax_rate_id), 10) : null
            return {
              description: row.description || "",
              amount_cents: amountToCents(row.amount),
              hsn_sac: row.hsn_sac || null,
              gst_tax_rate_id: gid && !Number.isNaN(gid) ? gid : null,
            }
          })
          .filter((row) => row.amount_cents > 0 || String(row.description || "").trim().length > 0)
      }

      if (sectionKey === "vendor-credits") {
        payload.lines = (form.lines || [])
          .map((row) => {
            const gid = row.gst_tax_rate_id ? Number.parseInt(String(row.gst_tax_rate_id), 10) : null
            return {
              description: row.description || "",
              amount_cents: amountToCents(row.amount),
              hsn_sac: row.hsn_sac || null,
              gst_tax_rate_id: gid && !Number.isNaN(gid) ? gid : null,
            }
          })
          .filter((row) => row.amount_cents > 0 || String(row.description || "").trim().length > 0)
        const lineSum = (payload.lines || []).reduce((s, row) => s + row.amount_cents, 0)
        payload.amount_cents = lineSum
        if (lineSum <= 0) {
          setFieldErrors({ _lines: "Add at least one line with an amount greater than zero." })
          throw new Error("Add line items with amounts.")
        }
      }

      const body = JSON.stringify({ [config.wrap]: payload })

      if (editingId) {
        await fetchJson(`${config.endpoint}/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson(config.endpoint, { method: "POST", body })
      }

      await reload()
      resetForm()
    } catch (e2) {
      setError(e2.message || "Save failed")
    }
  }

  function openCreateCustomerQuote(vendorQuote) {
    if (vendorQuote.status === "converted") {
      setError("This vendor quote was already converted to a customer quote.")
      return
    }
    if (!customers.length) {
      setError("Add a customer under Sales → Customers before creating an outbound quote.")
      return
    }
    setCustomerQuoteError("")
    setPendingCustomerQuote(vendorQuote)
  }

  async function confirmCreateCustomerQuote(customerId) {
    if (!pendingCustomerQuote || Number.isNaN(customerId)) return
    try {
      setCustomerQuoteSaving(true)
      setCustomerQuoteError("")
      const body = JSON.stringify({
        vendor_quote_id: pendingCustomerQuote.id,
        customer_id: customerId,
        quote_date: todayISO(),
      })
      const quote = await fetchJson("/api/sales/quotes/from_vendor_quote", { method: "POST", body })
      setPendingCustomerQuote(null)
      await reload()
      const num = quote?.quote_number || "saved"
      setError("")
      appToast(`Customer quote ${num} created. Open Sales → Quotes to review or send PDF.`)
    } catch (e) {
      setCustomerQuoteError(e.message || "Could not create customer quote")
    } finally {
      setCustomerQuoteSaving(false)
    }
  }

  async function onDelete(id) {
    if (!(await appConfirm("Delete this record?", { title: "Delete", danger: true, confirmLabel: "Delete" }))) return
    try {
      setError("")
      await fetchJson(`${config.endpoint}/${id}`, { method: "DELETE" })
      await reload()
      if (editingId === id) resetForm()
    } catch (e) {
      setError(e.message || "Delete failed")
    }
  }

  const vendorOptions = vendors.map((v) => ({ value: String(v.id), label: v.name }))
  const filteredItems = useMemo(() => {
    if (!query.trim()) return items
    const q = query.trim().toLowerCase()
    return items.filter((item) =>
      config.columns.some((c) => {
        let value = item[c]
        if (c === "vendor") value = item.vendor?.name || vendorNameFor(item.vendor_id)
        if (c === "quote_type") {
          value = QUOTE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value
        }
        if (c.endsWith("_cents")) value = centsToAmount(value)
        return String(value ?? "")
          .toLowerCase()
          .includes(q)
      })
    )
  }, [items, query, config, vendors])
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pagedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  if (!config) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">This section is not available.</p>
        </CardContent>
      </Card>
    )
  }

  if (sectionKey === "vendor-quotes" && !enableVendorQuoteReselling) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Vendor quote reselling is disabled for this company. An Admin or MD can enable it under Accountant → Bookkeeping.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <CreateCustomerQuoteDialog
        vendorQuote={pendingCustomerQuote}
        customers={customers}
        saving={customerQuoteSaving}
        error={customerQuoteError}
        onClose={() => {
          setPendingCustomerQuote(null)
          setCustomerQuoteError("")
        }}
        onConfirm={confirmCreateCustomerQuote}
      />
      {canEnter ? (
      <Card id="book-primary-form">
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{editingId ? `Edit ${sectionTitle}` : `Create ${sectionTitle}`}</CardTitle>
          {editingId ? (
            <Button type="button" variant="ghost" onClick={resetForm}>
              Cancel edit
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {error ? <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
          <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
            {config.fields.map((f) => {
              const value = form[f.name] ?? (f.kind === "boolean" ? false : "")
              if (f.kind === "vendor") {
                return (
                  <SelectInput
                    key={f.name}
                    label={f.label}
                    value={value}
                    onChange={(v) => setField(f.name, v)}
                    options={vendorOptions}
                    error={fieldErrors[f.name]}
                  />
                )
              }
              if (f.kind === "select") {
                return (
                  <SelectInput
                    key={f.name}
                    label={f.label}
                    value={value}
                    onChange={(v) => setField(f.name, v)}
                    options={f.options || []}
                    error={fieldErrors[f.name]}
                  />
                )
              }
              if (f.kind === "boolean") {
                return (
                  <label key={f.name} className="flex items-center gap-2 rounded-lg border border-border/90 bg-card px-2 py-1.5 text-xs shadow-sm">
                    <input type="checkbox" checked={Boolean(value)} onChange={(e) => setField(f.name, e.target.checked)} />
                    <span className="text-foreground/85">{f.label}</span>
                  </label>
                )
              }
              return (
                <TextInput
                  key={f.name}
                  label={f.label}
                  type={f.kind === "date" ? "date" : f.kind === "number" ? "number" : "text"}
                  value={value}
                  placeholder={f.placeholder}
                  onChange={(v) => setField(f.name, v)}
                  error={fieldErrors[f.name]}
                />
              )
            })}
            {sectionKey === "bills" ? (
              <DocumentLineRows
                value={form.lines}
                onChange={(lines) => setForm((prev) => ({ ...prev, lines }))}
                gstRateOptions={gstRateSelectOptions}
              />
            ) : null}
            {sectionKey === "vendor-credits" ? (
              <div className="md:col-span-2 grid gap-1">
                <DocumentLineRows
                  value={form.lines}
                  onChange={(lines) => setForm((prev) => ({ ...prev, lines }))}
                  gstRateOptions={gstRateSelectOptions}
                />
                {fieldErrors._lines ? <span className="text-[12px] text-red-600">{fieldErrors._lines}</span> : null}
              </div>
            ) : null}
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit">{editingId ? "Save" : "Create"}</Button>
              <Button type="button" variant="ghost" onClick={() => reload()}>
                Refresh
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      ) : (
      <Card>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">
            Your role (<span className="font-medium capitalize">{access.access_role || "viewer"}</span>) is read-only here — you cannot create or edit{" "}
            {sectionTitle.toLowerCase()}.
          </p>
          {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{sectionTitle} List</CardTitle>
          <div className="text-xs text-muted-foreground">{loading ? "Loading…" : `${filteredItems.length} record(s)`}</div>
        </CardHeader>
        <CardContent>
          {error && canEnter ? (
            <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div>
          ) : null}
          <div className="mb-2 max-w-sm">
            <TextInput label="Search" value={query} onChange={setQuery} placeholder="Filter list..." />
          </div>
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                  {config.columns.map((c) => (
                    <th key={c} className="px-2 py-1.5 font-medium">
                      {c === "vendor" ? "Vendor" : c.replaceAll("_", " ")}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((item) => (
                  <tr key={item.id} className="border-b border-border/60 bg-card last:border-0 hover:bg-muted/20">
                    {config.columns.map((c) => {
                      let value = item[c]
                      if (c === "vendor") value = item.vendor?.name || vendorNameFor(item.vendor_id)
                      if (c === "quote_type") {
                        value = QUOTE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value
                      }
                      if (c.endsWith("_cents")) value = centsToAmount(value)
                      if (typeof value === "boolean") value = value ? "Yes" : "No"
                      return (
                        <td key={c} className="px-2 py-1 text-foreground/85">
                          {value ?? ""}
                        </td>
                      )
                    })}
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap items-center gap-1">
                        {canEnter ? (
                          <>
                            <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs" onClick={() => hydrateForEdit(item)}>
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className={cn("h-7 px-1.5 text-xs text-red-600 hover:text-red-700")}
                              onClick={() => onDelete(item.id)}
                            >
                              Delete
                            </Button>
                          </>
                        ) : null}
                        {canEnter && sectionKey === "vendor-quotes" && item.status === "received" ? (
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-7 px-1.5 text-xs text-primary"
                            onClick={() => openCreateCustomerQuote(item)}
                          >
                            Create customer quote
                          </Button>
                        ) : null}
                        {item.sales_quote?.quote_number ? (
                          <span className="text-[11px] text-muted-foreground">{item.sales_quote.quote_number}</span>
                        ) : null}
                        {canApprove && sectionKey === "bills" && canApproveDocument(access, item) ? (
                          <>
                            {item.approval_status !== "approved" ? (
                              <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs text-primary" onClick={() => approveBill(item.id)}>
                                Approve
                              </Button>
                            ) : null}
                            {item.approval_status !== "rejected" ? (
                              <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs text-amber-700" onClick={() => rejectBill(item.id)}>
                                Reject
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && filteredItems.length === 0 ? (
                  <tr>
                    <td className="px-2 py-3 text-muted-foreground" colSpan={config.columns.length + 1}>
                      No records yet. Create your first one above.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}>
            Previous
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-8 px-2"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}

export function PurchasesPage(props) {
  const {
    section,
    section_title,
    active_slug,
    user_name,
    enable_vendor_quote_reselling: enableVendorQuoteReselling = false,
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
    current_organization,
  } = props

  const access = accessFromProps(props)
  const activeKey = section || "vendors"

  return (
    <BookShell
      activeSlug={active_slug || "purchases"}
      userName={user_name}
      access={access}
      featureFlags={{ vendor_quote_reselling: Boolean(enableVendorQuoteReselling) }}
    >
      <div className="flex flex-col gap-6">
        <PageHeader
          context="Purchases"
          title={section_title}
          actions={
            <>
              <a href="#" className="hidden text-[14px] font-medium text-primary hover:underline sm:inline">
                Find accountants
              </a>
              {activeKey !== "requisitions" ? (
                <Button
                  type="button"
                  className="gap-1 px-3"
                  onClick={() => document.getElementById("book-primary-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  <span className="text-base leading-none">+</span>
                  New
                </Button>
              ) : null}
            </>
          }
        />
        {activeKey === "requisitions" ? (
          <RequisitionsPanel
            canEnter={Boolean(can_enter_transactions) && !is_viewer}
            currentOrganization={current_organization}
          />
        ) : (
          <ResourceCrud
            sectionKey={activeKey}
            sectionTitle={section_title}
            access={access}
            enableVendorQuoteReselling={Boolean(enableVendorQuoteReselling)}
          />
        )}
      </div>
    </BookShell>
  )
}
